import type { CSSProperties, ReactElement, ReactNode } from 'react';
import React from 'react';
import styled from 'types/theme.types';
import { FormattedMessage } from 'react-intl';
import { Skeleton } from 'components/common/skeleton';
import { BridgedAlert as Alert } from 'components/common/feedback/bridged-alert';
import { css, useTheme } from '@emotion/react';
import Link from 'components/common/link';
import { legacyOnly, newDesignOnly, type Env0Theme } from 'constants/themes.constants';
import { typeStyle } from 'constants/typography';

export const headerPaddingCss = css`
  padding: 15px 24px;
  position: relative;
`;

export const headerFontCss = css`
  font-size: 18px;
  font-weight: 500;
`;

interface Props {
  id?: string;
  title?: ReactElement | ReactNode;
  style?: CSSProperties;
  inline?: boolean;
  inlineBottomBorder?: boolean;
  withTitleDivider?: boolean;
  loading?: boolean;
  className?: string;
  allowBodyScroll?: boolean;
  fullWidthContent?: boolean;
  vertical?: boolean;
  itemGap?: number | string;
  children?: ReactNode;
  /** Lifted off antd Card for BridgedCard's call sites. Hover moves the border, never a shadow. */
  hoverable?: boolean;
  /** antd Card's cover slot: rendered between the header and the body. */
  cover?: ReactNode;
  /** antd Card's bodyStyle.padding. New-design only - see newDesignPadding. */
  bodyPadding?: string;
}

/**
 * Every branch below is hoisted into a props-taking function rather than nested inside a css block: an
 * interpolation inside css receives only the theme, not the component's props. Same shape
 * buttons-container.tsx uses for its isInline split.
 *
 * Slot ORDER is load-bearing. The switch-off fixture recorded the declarations in source order, so a
 * legacy slot has to stay where it was - hence one branch pair per property group rather than one pair
 * emitting several groups together.
 */
type ContainerProps = { inline?: boolean; inlineBottomBorder?: boolean; hoverable?: boolean; fullBleed?: boolean };
type ContainerArgs = ContainerProps & { theme: Env0Theme };

const legacyContainer = (props: ContainerArgs) =>
  legacyOnly(
    theme => css`
      ${
        props.inline
          ? props.inlineBottomBorder
            ? ''
            : `border-bottom: 1px solid ${theme.lightGray}`
          : `border: 1px solid ${theme.lightGray}`
      };
      border-radius: ${props.inline ? 'none' : '4px'};
      background: ${theme.primaryWhite};
    `
  )(props);

const newDesignContainer = (props: ContainerArgs) =>
  newDesignOnly(
    theme => css`
      /* antd's card root is position: relative, and three of BridgedCard's call sites put an absolutely
         positioned info icon inside the card and relied on it for their containing block. Without this the
         icon escapes to whatever ancestor is positioned and lands at the far edge of the page. New-design
         only, so the switch-off box is untouched. */
      position: relative;

      ${
        props.inline
          ? props.inlineBottomBorder
            ? ''
            : `border-block-end: ${theme.borderWidth.xs} solid var(--border-primary)`
          : `border: ${theme.borderWidth.xs} solid var(--border-primary)`
      };
      border-radius: ${props.inline ? theme.radius.none : theme.radius['400']};
      background: var(--surface-main-cards);

      /* Full-bleed bodies (fullWidthContent) put square-cornered panels flush against the card edge —
         the inner box painted straight over the 12px border arc, which read as a missing corner
         (UI-polish review, item C). Clipping is scoped to full-bleed so the absolutely-positioned
         info icons other call sites hang outside the card keep escaping. */
      ${props.fullBleed ? 'overflow: hidden;' : ''}

      /* No card shadow exists in the token set or on any Figma frame, so hover moves the border colour.
         antd's own .ant-card-hoverable:hover raises a three-layer rgba shadow instead - not reachable
         from the bridge, which is why hover is owned here. */
      ${
        props.hoverable
          ? css`
              cursor: pointer;

              &:hover {
                border-color: var(--border-action);
              }
            `
          : ''
      }
    `
  )(props);

const Container = styled('div')<ContainerProps>`
  display: flex;
  flex-direction: column;
  ${legacyContainer}
  ${newDesignContainer}
`;

type HeaderArgs = { divider: boolean; theme: Env0Theme };

const legacyHeader = (props: HeaderArgs) =>
  legacyOnly(
    theme => css`
      ${headerPaddingCss};
      ${headerFontCss};
      border-bottom: ${props.divider ? `1px solid ${theme.lightGray}` : 'none'};
    `
  )(props);

const newDesignHeader = (props: HeaderArgs) =>
  newDesignOnly(
    theme => css`
      position: relative;
      /* The frames measure 20px top and 22px inline; 22 is not a rung on the ladder, so the inline pair
         rounds up to the nearest one. Raised with design as a Figma-side fix. */
      padding: ${theme.padding.xl} ${theme.padding.xxl};
      /* Heading-h3, per every card title in the frames (267:7840 "Environment Details"). */
      ${typeStyle.headingH3}
      color: var(--text-heading);
      border-block-end: ${props.divider ? `${theme.borderWidth.xs} solid var(--border-primary)` : 'none'};
    `
  )(props);

export const Header = styled.div<{ divider: boolean }>`
  ${legacyHeader}
  ${newDesignHeader}
`;

type BodyProps = {
  allowBodyScroll?: boolean;
  noPadding?: boolean;
  itemGap: Props['itemGap'];
  vertical?: boolean;
  bodyPadding?: string;
};
type BodyArgs = BodyProps & { theme: Env0Theme };

const legacyPadding = (props: BodyArgs) =>
  legacyOnly(
    props.noPadding
      ? css``
      : css`
          padding: 15px 24px;
        `
  )(props);

/**
 * bodyPadding has no legacy effect on purpose: no legacy call site passes it, so honouring it on both
 * sides could only move switch-off. It exists for the .ant-card-body overrides BridgedCard's call sites
 * used to carry.
 */
const newDesignPadding = (props: BodyArgs) =>
  newDesignOnly(
    theme => css`
      ${props.noPadding ? '' : `padding: ${props.bodyPadding ?? `${theme.padding.xl} ${theme.padding.xxl}`};`}
    `
  )(props);

/**
 * The gap ignores itemGap's value on both sides, which is pre-existing: the legacy body hardcoded 15px
 * and never read the prop. The new design emits the ladder's 16px, matching the frames' itemSpacing.
 */
const legacyGap = (props: BodyArgs) =>
  legacyOnly(
    props.itemGap
      ? css`
          gap: 15px;
        `
      : css``
  )(props);

const newDesignGap = (props: BodyArgs) =>
  newDesignOnly(theme =>
    props.itemGap
      ? css`
          gap: ${theme.spacing.lg};
        `
      : css``
  )(props);

const Body = styled.div<BodyProps>`
  ${legacyPadding}
  ${newDesignPadding}
  ${props => props.allowBodyScroll && 'overflow-y: auto;'}
  ${props => props.itemGap && 'display: flex;'}
  ${legacyGap}
  ${newDesignGap}
  ${props => props.vertical && 'flex-direction: column;'}
`;

const Card: React.FC<Props> = ({
  id,
  title,
  children,
  style,
  inline,
  inlineBottomBorder = false,
  withTitleDivider = true,
  loading,
  allowBodyScroll = false,
  fullWidthContent,
  itemGap,
  vertical,
  hoverable,
  cover,
  bodyPadding,
  ...props
}) => {
  return (
    <Container
      id={id}
      style={style}
      inline={inline}
      inlineBottomBorder={inline && inlineBottomBorder}
      hoverable={hoverable}
      fullBleed={fullWidthContent}
      {...props}>
      {title && <Header divider={withTitleDivider}>{title}</Header>}
      {cover}
      <Body
        className="custom-card-body"
        allowBodyScroll={allowBodyScroll}
        noPadding={fullWidthContent}
        itemGap={itemGap}
        vertical={vertical}
        bodyPadding={bodyPadding}>
        {loading ? <Skeleton /> : children}
      </Body>
    </Container>
  );
};

interface CardHeaderProps {
  titleId: string;
  titleValues?: Record<string, React.ReactNode>;
  /** Optional: a header can be a bare title-plus-action row (the keys and API-keys cards). */
  descriptionId?: string;
  descriptionValues?: Record<string, React.ReactNode | ((parts: string | ReactNode) => ReactNode)>;
  link?: string;
  linkMessageId?: string;
  warning?: string;
  descriptionExtensionComponent?: React.ReactElement; // Used to display additional information in the description
  headerAction?: React.ReactNode;
  titleAction?: React.ReactNode;
}

export const CardHeader: React.FC<CardHeaderProps> = ({
  titleId,
  titleValues,
  descriptionId,
  descriptionValues,
  link,
  linkMessageId = 'link.explanation.more',
  warning,
  descriptionExtensionComponent,
  headerAction,
  ...props
}) => {
  const theme = useTheme();
  return (
    <CardHeaderContainer style={{ width: '100%' }} {...props}>
      <TextContent>
        {/* react-intl renders bare text, so the hook callers can restyle the title through lives on
            the container - FormattedMessage swallows its own data-e2e. */}
        <TitleContainer data-e2e="card-header-title">
          <FormattedMessage data-e2e="card-header-text" id={titleId} values={titleValues} />
          {/* New design centres the action against the whole text block below; legacy keeps it on the title row. */}
          {!theme.isNewDesign && headerAction}
        </TitleContainer>
        {(descriptionId || warning || descriptionExtensionComponent) && (
          <CardDescription>
            {descriptionId && (
              <>
                <FormattedMessage id={descriptionId} values={descriptionValues} />{' '}
              </>
            )}
            {link && (
              <Link url={link}>
                <FormattedMessage id={linkMessageId} />
              </Link>
            )}
            {warning && <StyledAlert message={<FormattedMessage id={warning} />} showIcon closable />}
            {descriptionExtensionComponent}
          </CardDescription>
        )}
      </TextContent>
      {theme.isNewDesign && headerAction && (
        <HeaderActionSlot data-e2e="card-header-action">{headerAction}</HeaderActionSlot>
      )}
    </CardHeaderContainer>
  );
};

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  ${newDesignOnly(
    theme => css`
      gap: ${theme.spacing.xxs};
    `
  )}
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const CardDescription = styled.div`
  margin-top: 10px;

  ${newDesignOnly(css`
    margin-top: 0;
  `)}

  /* Branched in place, NOT appended: the switch-off fixture records declarations in source order, so
     moving color past font-size/weight is a byte change even with identical values - the parity gate
     caught exactly that. primaryBlack is a legacy Env0Theme key the new-design path may not read (D7.3);
     the size and weight stay put because the type ramp is A3's, not this ticket's. */
  ${legacyOnly(
    theme => css`
      color: ${theme.primaryBlack};
    `
  )}
  ${newDesignOnly(css`
    color: var(--text-body);
  `)}

  font-size: 13px;
  font-weight: 100;
  white-space: pre-wrap;
`;

const StyledAlert = styled(Alert)`
  margin-top: 10px;
`;

const CardHeaderContainer = styled.div`
  width: 100%;
  display: flex;

  ${newDesignOnly(
    theme => css`
      align-items: center;
      gap: ${theme.spacing.xl};
    `
  )}
`;

const HeaderActionSlot = styled.div`
  flex-shrink: 0;
`;

export default Card;
